'use client';

import { unzip } from 'unzipit';
import { XMLParser } from 'fast-xml-parser';

export type MusicXMLParseQuery = Record<string, string>;
export type MusicXMLParseResult = {
  musicXml: string;
  queries: Record<string, { query: string; result: any }>;
};

const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  preserveOrder: true
});

export async function parseMusicXML(
  musicXmlOrBuffer: ArrayBuffer | string,
  queries?: MusicXMLParseQuery,
): Promise<MusicXMLParseResult> {
  if (musicXmlOrBuffer instanceof ArrayBuffer) {
    // Decode the buffer and try it as an uncompressed document.
    const musicXml = new TextDecoder().decode(musicXmlOrBuffer);
    try {
      return await _parseUncompressedMusicXML(musicXml, queries);
    } catch {
      // Do nothing: just keep going.
    }

    // Try the buffer as a compressed document.
    return await _parseCompressedMusicXML(musicXmlOrBuffer, queries);
  } else {
    // A string is assumed to be an uncompressed document.
    return await _parseUncompressedMusicXML(musicXmlOrBuffer, queries);
  }
}

export async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

async function _parseCompressedMusicXML(
  mxml: ArrayBuffer,
  queries: MusicXMLParseQuery | undefined,
): Promise<MusicXMLParseResult> {
  const { entries } = await unzip(mxml);

  // Extract rootfile from META-INF/container.xml.
  const decoder = new TextDecoder();
  const containerBuf = await entries['META-INF/container.xml'].arrayBuffer();
  const containerXml = decoder.decode(containerBuf);
  const containerDoc = parser.parse(containerXml);
  
  // Find the rootfile path
  const rootFile = findRootFilePath(containerDoc);
  if (!rootFile) {
    throw new Error('Invalid compressed MusicXML file does not contain rootfile/@full-path.');
  }

  // Parse root document as MusicXML.
  const rootBuf = await entries[rootFile].arrayBuffer();
  return _parseUncompressedMusicXML(decoder.decode(rootBuf), queries);
}

function findRootFilePath(containerDoc: any): string | undefined {
  // Navigate through the parsed XML to find the rootfile path
  const container = containerDoc.find((item: any) => item['container'])?.[0];
  const rootfiles = container?.rootfiles?.[0];
  const rootfile = rootfiles?.rootfile?.[0];
  return rootfile?.['@_full-path'];
}

async function _parseUncompressedMusicXML(
  musicXml: string,
  queries: MusicXMLParseQuery | undefined,
): Promise<MusicXMLParseResult> {
  const doc = parser.parse(musicXml);
  
  // Find score-partwise version
  const scorePartwise = doc.find((item: any) => item['score-partwise'])?.[0];
  const version = scorePartwise?.['@_version'] || '(unknown)';
  console.debug(`[parseMusicXML] MusicXML version ${version}`);

  const parseResult: MusicXMLParseResult = {
    musicXml,
    queries: {},
  };

  if (queries) {
    for (const [key, query] of Object.entries(queries)) {
      try {
        const result = evaluateXPath(doc, query);
        parseResult.queries[key] = {
          query,
          result: result || null,
        };
      } catch {
        parseResult.queries[key] = {
          query,
          result: null,
        };
      }
    }
  }

  return parseResult;
}

// Simple XPath-like evaluation function
function evaluateXPath(doc: any, xpath: string): any {
  const parts = xpath.split('/').filter(Boolean);
  let current = doc;

  for (const part of parts) {
    if (!current) return null;

    if (part === '//') {
      // Handle recursive search (simplified)
      // This is a basic implementation and might need to be enhanced
      continue;
    }

    if (part.includes('@')) {
      // Handle attribute
      const attrName = '@_' + part.replace('@', '');
      return current[attrName];
    }

    if (part.includes('[')) {
      // Handle array index
      const [name, indexStr] = part.split('[');
      const index = parseInt(indexStr.replace(']', '')) - 1;
      current = current.find((item: any) => item[name])?.[index];
    } else {
      // Handle regular element
      current = current.find((item: any) => item[part])?.[0];
    }
  }

  return current;
}
